/**
 * Print/Preview
 */
import vuetify from '@/plugins/vuetify';
import { useNotificationStore } from '@/stores/notification';
import { openPreviewWindow, renderToPreviewWindow, DOCUMENT_PREVIEW_PATH } from '@/common/printPreviewWindow';

var print = {};

// Separates stacked forms in a print job, and the marker the no-content check below strips
// back out. Exported because the snapshot print paths emit the same marker; a divergent copy
// silently stops breaking pages.
export const PAGE_BREAK = '<div class="page-break"></div>';

/**
 * Apply the shared static-HTML transform to a single cloned element:
 *   - stringify <input>/<textarea> values
 *   - mark v-fields active so empty fields render with their label
 *   - turn ProseMirror rich text editors read-only
 * Preserves data-* attributes (data-signer-placeholder, data-signer-task-id,
 * data-appendages, data-appendage-index, etc.) through the clone — these are
 * the reserved-region markers used by formSnapshot capture and sub-issue C.
 */
function applyStaticHtmlTransform(clonedElement) {
    if (!clonedElement) return;

    let inputElems = clonedElement.getElementsByTagName('input');
    Array.from(inputElems).forEach((input) => {
        input.setAttribute('value', input.value);
        input.removeAttribute('placeholder');
    });

    let textareaElems = clonedElement.getElementsByTagName('textarea');
    Array.from(textareaElems).forEach((textarea) => {
        textarea.innerHTML = textarea.value;
        textarea.removeAttribute('placeholder');
    });

    let fieldElems = clonedElement.getElementsByClassName('v-field');
    Array.from(fieldElems).forEach((field) => {
        field.classList.add('v-field--active');
    });

    let richTextElems = clonedElement.getElementsByClassName('ProseMirror');
    Array.from(richTextElems).forEach((editor) => {
        editor.setAttribute('contenteditable', false);
    });
}

/**
 * Transform a single rendered form root into a static body fragment, wrapped in
 * the `.bn-snapshot-wrap` container. Returns ONLY the fragment — no doctype/head/
 * body — so callers can compose one or more fragments into a single document via
 * wrapSnapshotDocument(). Group snapshots stack the group section plus every
 * participant section this way; a non-group form yields a single fragment.
 *
 * Reactive bindings are serialized to plain markup and <script> tags stripped.
 * Reserved-region data-* attributes survive the clone so sub-issue C can mutate
 * against them.
 *
 * @param {HTMLElement} rootElement - the rendered form body to capture
 * @returns {string} `.bn-snapshot-wrap` fragment, or '' when rootElement is null
 */
export function captureSnapshotBody(rootElement) {
    if (!rootElement) return '';

    const cloned = rootElement.cloneNode(true);
    applyStaticHtmlTransform(cloned);

    const scripts = cloned.querySelectorAll('script');
    scripts.forEach((s) => s.parentNode?.removeChild(s));

    // Items the form hid rather than dropped. An item gated off by enableWhen stays in the DOM
    // under `v-show`, which hides it by setting `display: none` inline. This HTML is the stored
    // record, so that markup — and any answer typed before the gate closed over it — would
    // travel into the record unseen by the person signing it. The print tree states the same
    // rule for a field set hidden on lock by leaving it out rather than hiding it.
    //
    // Inline style only. `d-none` and the print-only/screen-only classes are how the document
    // hides what it means to keep, and they carry no inline display of their own.
    const hidden = cloned.querySelectorAll('[style*="display"]');
    hidden.forEach((element) => {
        if (element.style?.display === 'none') element.parentNode?.removeChild(element);
    });

    // The wrapper carries no class but its own. It used to borrow .bn-form-container for that
    // rule's max-width, which also brought the card border the print path had deliberately
    // turned off (show-form-border=false) — so every snapshot was boxed, on screen and in
    // print. .bn-snapshot-wrap owns its width in printSnapshot.scss instead.
    return `<div class="bn-snapshot-wrap">${cloned.outerHTML || ''}</div>`;
}

/**
 * Wrap one or more `.bn-snapshot-wrap` fragments in the self-contained snapshot
 * document shell (sections container + print CSS link).
 * Group snapshots stack several fragments here so every section (group + each
 * participant) renders in one document — a browser keeps only the first <html>
 * root when handed multiple full documents.
 *
 * CSS strategy: links to /print/assets/print-document.css, the stylesheet that owns
 * the printed record. It carries the print design system, the item-visibility rules
 * and Poppins, and deliberately no app CSS — the print component tree emits no Vuetify
 * component markup, so the 467 KB of vuetify.min.css the old bundle carried was rules
 * this document does not want. This URL is resolved against the embedding page when
 * rendered via srcdoc. Inlining it — which bn-pdf requires, and which 41 KB makes
 * tractable where 655 KB did not — is still to come.
 *
 * @param {string} bodyHtml - one or more `.bn-snapshot-wrap` fragments
 * @returns {string} static HTML document
 */
export function wrapSnapshotDocument(bodyHtml) {
    return [
        '<!doctype html>',
        '<html lang="en">',
        '<head>',
        '<meta charset="UTF-8" />',
        '<meta name="viewport" content="width=device-width, initial-scale=1" />',
        '<title>Form snapshot</title>',
        '<link rel="stylesheet" href="/print/assets/print-document.css" type="text/css" />',
        // Each section on white, the page behind it grey. No border: a record is not a card, and
        // this sheet is written into the document, so it overrode the rule in printSnapshot.scss
        // that dropped the card border everywhere else.
        // pointer-events:none makes the snapshot read-only by appearance — no hover states,
        // no click feedback, no focus rings on form fields — reinforcing that it's an
        // immutable rendering. Text selection via keyboard still works for copy/paste.
        // Stacked sections (group + participants) get a gap between them.
        '<style>html,body{margin:0;padding:0;background:#f5f5f5;}body{padding:16px;}.bn-snapshot-wrap{max-width:960px;background:#fff;pointer-events:none;}.bn-snapshot-wrap ~ .bn-snapshot-wrap{margin-top:16px;}</style>',
        '</head>',
        '<body>',
        // The sections' container, and the hook mergeSnapshotHtml appends a participant into.
        // It used to be Vuetify's `.v-application__wrap` inside a `.v-application` body, which
        // was doing real work: `.v-application` is display:flex (row), so the wrap's flex column
        // was what stopped stacked sections laying out side by side. The document loads no
        // Vuetify now, so body is an ordinary block and its children stack on their own; the
        // container stays because the merge needs something to append into.
        '<div class="bn-snapshot-doc">',
        bodyHtml || '',
        '</div>',
        '</body>',
        '</html>',
    ].join('');
}

/**
 * Capture a self-contained static HTML snapshot of a single rendered form root.
 * Convenience wrapper: a `.bn-snapshot-wrap` fragment inside the document shell.
 * Used by formSnapshot.captureSnapshot() at form-lock time for non-group forms.
 *
 * @param {HTMLElement} rootElement - the rendered form body to capture
 * @returns {string} static HTML document
 */
export function captureSnapshot(rootElement) {
    if (!rootElement) return '';
    return wrapSnapshotDocument(captureSnapshotBody(rootElement));
}

// Everything up to the window handshake is synchronous on purpose: callers such as
// RoiWidget tear the source container out of the DOM the moment this returns, so an
// await before the clone would capture nothing.
print.preview = function (sourceElement, config, previewPath = '/print/preview.html', preProcessor = undefined) {
    let html = '';
    // One footer per printed section, in document order — see markSection.
    const sectionFooters = [];

    if (config.configs) {
        // For each config, process its form
        config.configs.forEach((formConfig, index) => {
            let formElements = document.getElementById('printFormView' + formConfig.id)?.getElementsByClassName(sourceElement);
            if (!formElements) return;

            let clonedFormElements = Array.from(formElements).map((el) => el.cloneNode(true));
            clonedFormElements.forEach((clonedElement, elementIndex) => {
                applyStaticHtmlTransform(clonedElement);
                markSection(clonedElement, sectionFooters.length);
                sectionFooters.push(formConfig.sectionFooters?.[elementIndex] || formConfig.footers || null);
                html += clonedElement?.innerHTML?.toString() || '';
                // A group task holds one record per participant, and each starts its own page as
                // it does printed alone — a page shared by two records has only one footer.
                if (elementIndex < clonedFormElements.length - 1) html += PAGE_BREAK;
            });

            if (index < config.configs.length - 1) {
                html += PAGE_BREAK;
            }
        });
    } else {
        // Original single form
        let sourceElements = [document.getElementById(sourceElement)];
        let clonedSourceElements = [];

        if (sourceElements[0] === null) {
            const scopedElements = document.getElementById('printFormView' + config.id)?.getElementsByClassName(sourceElement);
            sourceElements = scopedElements ? Array.from(scopedElements) : [];
        }

        sourceElements.forEach((el) => clonedSourceElements.push(el.cloneNode(true)));

        // If a preprocessor function was passed, call it
        if (typeof preProcessor === 'function') {
            clonedSourceElements.forEach((element) => {
                preProcessor(element);
            });
        }

        // Between elements, not after the last one — a trailing break starts a page that
        // nothing then fills, and the reader gets a blank sheet at the end of the document.
        clonedSourceElements.forEach((clonedSourceElement, index) => {
            applyStaticHtmlTransform(clonedSourceElement);
            markSection(clonedSourceElement, index);
            sectionFooters.push(config.sectionFooters?.[index] || config.footers || null);
            html += clonedSourceElement?.innerHTML?.toString() || '';
            if (index < clonedSourceElements.length - 1) html += PAGE_BREAK;
        });
    }

    // A missing source container yields an empty-but-truthy HTMLCollection, so the assembled
    // html is the only place "no content" is distinguishable — and page-break markers, added
    // per element, are not content on their own.
    const printableContent = html.split(PAGE_BREAK).join('').trim();
    if (!printableContent) {
        console.error('Print preview has no content to render:', config?.id ? 'config id: ' + config.id : '');
        useNotificationStore().add({
            message: 'There is nothing to print.',
            color: 'error',
            timeout: 8000,
        });
        return Promise.resolve(false);
    }

    // Only the app-DOM paths take the theme. The form print tree must never use theme classes or
    // variables — a stored snapshot carries no theme — and the document window refuses one anyway.
    let themeStyling = previewPath === DOCUMENT_PREVIEW_PATH ? '' : vuetify.theme.styles.value;

    // The job's header and footer come from its first form. A job of several sections also sends
    // each section's own footer, so every page names the patient whose record it is.
    const jobConfig = config.configs?.[0] || config;
    const namesOthers = sectionFooters.some((footers) => (footers || null) !== (jobConfig.footers || null));
    const previewConfig = namesOthers ? { ...jobConfig, sectionFooters } : jobConfig;
    return renderToPreviewWindow(openPreviewWindow(previewPath), previewConfig, html, themeStyling);
};

/**
 * Stamp a section's first element with its index, for print-preview.js to open that section with
 * its own footer. The clone's own root is not carried — only its innerHTML is — so the mark goes
 * one level down.
 */
function markSection(clonedElement, index) {
    clonedElement?.firstElementChild?.setAttribute('data-print-section', String(index));
}

export default print;
